/**
 * Subscription / credit-pack configuration.
 * Monthly  = auto-renewing monthly subscription via Cashfree.
 * Quarterly = auto-renewing 3-month subscription via Cashfree.
 * HalfYearly = auto-renewing 6-month subscription via Cashfree.
 * credits = number of resume optimizations included per billing period.
 */

export type CreditPack = {
  id: string;
  name: string;
  credits: number;        // optimizations per billing period
  totalCredits: number;   // total optimizations for the full term
  price: number;          // INR total charge per period
  effectiveMonthly: number; // INR effective per month (for display)
  period: string;         // e.g. '30 days', '3 months', '6 months'
  periodLabel: string;    // short label shown in price display
  autoRenew: boolean;
  savePct: string | null; // e.g. 'Save 28%' or null
  popular?: boolean;
  bonusCredits?: number;
};

export const CREDIT_PACKS: CreditPack[] = [
  {
    id: 'pack-monthly',
    name: 'Monthly',
    credits: 60,
    totalCredits: 60,
    price: 349,
    effectiveMonthly: 349,
    period: '30 days',
    periodLabel: 'per month',
    autoRenew: true,
    savePct: null,
    popular: true,
  },
  {
    id: 'pack-quarterly',
    name: 'Quarterly',
    credits: 60,
    totalCredits: 180,
    price: 749,
    effectiveMonthly: 250,
    period: '3 months',
    periodLabel: '/ 3 months',
    autoRenew: true,
    savePct: 'Save 28%',
    popular: false,
  },
  {
    id: 'pack-halfyearly',
    name: 'Half-Yearly',
    credits: 60,
    totalCredits: 360,
    price: 1499,
    effectiveMonthly: 250,
    period: '6 months',
    periodLabel: '/ 6 months',
    autoRenew: true,
    savePct: 'Save 28%',
    popular: false,
  },
];

export function getPackById(id: string): CreditPack | undefined {
  return CREDIT_PACKS.find((p) => p.id === id);
}

/** Credits actually granted on successful payment (use at checkout — not per-month display). */
export function getPackGrantCredits(pack: CreditPack): number {
  return pack.totalCredits + (pack.bonusCredits ?? 0);
}

/** Human-readable invoice line item for a billing plan purchase. */
export function formatPlanInvoiceDescription(planId?: string, creditsFallback?: number): string {
  if (planId) {
    const pack = getPackById(planId);
    if (pack) {
      return `GoGrabJob ${pack.name} Plan — ${getPackGrantCredits(pack)} credits (${pack.periodLabel})`;
    }
  }
  if (creditsFallback && creditsFallback > 0) {
    return `GoGrabJob subscription — ${creditsFallback} credits`;
  }
  return 'GoGrabJob subscription';
}

/** Convert stored transaction amount (paise) or rupee value to rupees for display/invoices. */
export function transactionAmountToRupees(amount: number | string | null | undefined): number {
  const n = Number(amount);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return n >= 1000 ? parseFloat((n / 100).toFixed(2)) : parseFloat(n.toFixed(2));
}
